// @ts-strict-ignore
import { Component } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { calculateResolution, ChronoUnit, Resolution } from "src/app/edge/historysignage/shared";
import { AbstractHistoryChart } from "src/app/shared/components/chart/abstracthistorychart";
import { ChartConstants } from "src/app/shared/components/chart/chart.constants";
import { JsonrpcResponseError } from "src/app/shared/jsonrpc/base";
import { QueryHistoricTimeseriesDataRequest } from "src/app/shared/jsonrpc/request/queryHistoricTimeseriesDataRequest";
import { QueryHistoricTimeseriesDataResponse } from "src/app/shared/jsonrpc/response/queryHistoricTimeseriesDataResponse";
import { QueryHistoricTimeseriesEnergyResponse } from "src/app/shared/jsonrpc/response/queryHistoricTimeseriesEnergyResponse";
import { ChannelAddress, EdgeConfig, Utils } from "src/app/shared/shared";
import { DateUtils } from "src/app/shared/utils/date/dateutils";
import { DateTimeUtils } from "src/app/shared/utils/datetime/datetime-utils";
import { ChartAxis, HistoryUtils, YAxisType } from "src/app/shared/utils/utils";
@Component({
    selector: "energychart",
    templateUrl: "../../../../../shared/components/chart/abstracthistorychart.html",
    standalone: false,
})
export class ChartComponent extends AbstractHistoryChart {

    public static getChartData(config: EdgeConfig | null, chartType: "line" | "bar", translate: TranslateService): HistoryUtils.ChartData {
        const input: HistoryUtils.InputChannel[] =
            config?.widgets.classes.reduce((arr: HistoryUtils.InputChannel[], key) => {
                const newObj = [];
                switch (key) {
                    case "Energymonitor":
                    case "Consumption":
                        newObj.push({
                            name: "Consumption",
                            powerChannel: new ChannelAddress("_sum", "ConsumptionActivePower"),
                            energyChannel: new ChannelAddress("_sum", "ConsumptionActiveEnergy"),
                        });
                        break;
                    case "Common_Autarchy":
                    case "Grid":
                        newObj.push({
                            name: "GridBuy",
                            powerChannel: new ChannelAddress("_sum", "GridActivePower"),
                            energyChannel: new ChannelAddress("_sum", "GridBuyActiveEnergy"),
                            ...(chartType === "line" && { converter: HistoryUtils.ValueConverter.NEGATIVE_AS_ZERO }),
                        }, {
                            name: "GridSell",
                            powerChannel: new ChannelAddress("_sum", "GridActivePower"),
                            energyChannel: new ChannelAddress("_sum", "GridSellActiveEnergy"),
                            ...(chartType === "line" && { converter: HistoryUtils.ValueConverter.POSITIVE_AS_ZERO_AND_INVERT_NEGATIVE }),
                        });
                        break;
                    case "Storage":
                        newObj.push({
                            name: "EssSoc",
                            powerChannel: new ChannelAddress("_sum", "EssSoc"),
                        }, {
                            name: "EssCharge",
                            powerChannel: new ChannelAddress("_sum", "EssActivePower"),
                            energyChannel: new ChannelAddress("_sum", "EssDcChargeEnergy"),
                        }, {
                            name: "EssDischarge",
                            powerChannel: new ChannelAddress("_sum", "EssActivePower"),
                            energyChannel: new ChannelAddress("_sum", "EssDcDischargeEnergy"),
                        });
                        break;
                    case "Common_Selfconsumption":
                    case "Common_Production":
                        newObj.push({
                            name: "ProductionActivePower",
                            powerChannel: new ChannelAddress("_sum", "ProductionActivePower"),
                            energyChannel: new ChannelAddress("_sum", "ProductionActiveEnergy"),
                        }, {
                            name: "ProductionDcActual",
                            powerChannel: new ChannelAddress("_sum", "ProductionDcActualPower"),
                            energyChannel: new ChannelAddress("_sum", "ProductionActiveEnergy"),
                        });
                        break;
                }

                arr.push(...newObj);
                return arr;
            }, []);

        return {
            input: input,
            output: (data: HistoryUtils.ChannelData): HistoryUtils.DisplayValue[] => {
                return [
                    {
                        name: translate.instant("GENERAL.PRODUCTION"),
                        nameSuffix: (energyValues: QueryHistoricTimeseriesEnergyResponse) => energyValues.result.data["_sum/ProductionActiveEnergy"],
                        converter: () => data["ProductionActivePower"],
                        color: ChartConstants.Colors.BLUE,
                        stack: 0,
                        hiddenOnInit: chartType == "line" ? false : true,
                        order: 1,
                    },

                    // DirectConsumption, displayed in stack 1 & 2, only one legenItem
                    ...[chartType === "bar" && {
                        name: translate.instant("GENERAL.DIRECT_CONSUMPTION"),
                        nameSuffix: (energyValues: QueryHistoricTimeseriesEnergyResponse) => {
                            return Utils.subtractSafely(energyValues.result.data["_sum/ProductionActiveEnergy"], energyValues.result.data["_sum/GridSellActiveEnergy"], energyValues.result.data["_sum/EssDcChargeEnergy"]);
                        },
                        converter: () =>
                            data["ProductionActivePower"]?.map((value, index) => Utils.subtractSafely(value, data["GridSell"]?.[index], data["EssCharge"]?.[index]))
                                ?.map(value => HistoryUtils.ValueConverter.NEGATIVE_AS_ZERO(value)),
                        color: ChartConstants.Colors.ORANGE,
                        stack: [1, 2],
                        order: 2,
                    }],

                    // Charge Power
                    {
                        name: translate.instant("GENERAL.CHARGE"),
                        nameSuffix: (energyValues: QueryHistoricTimeseriesEnergyResponse) => energyValues.result.data["_sum/EssDcChargeEnergy"],
                        converter: () => chartType === "line" //
                            ? data["EssCharge"]?.map((value, index) => {
                                return HistoryUtils.ValueConverter.POSITIVE_AS_ZERO_AND_INVERT_NEGATIVE(Utils.subtractSafely(value, data["ProductionDcActual"]?.[index]));
                            }) : data["EssCharge"],
                        color: ChartConstants.Colors.GREEN,
                        stack: 1,
                        ...(chartType === "line" && { order: 6 }),
                    },

                    // Discharge Power
                    {
                        name: translate.instant("GENERAL.DISCHARGE"),
                        nameSuffix: (energyValues: QueryHistoricTimeseriesEnergyResponse) => energyValues.result.data["_sum/EssDcDischargeEnergy"],
                        converter: () => {
                            return chartType === "line" ?
                                data["EssDischarge"]?.map((value, index) => {
                                    return HistoryUtils.ValueConverter.NEGATIVE_AS_ZERO(Utils.subtractSafely(value, data["ProductionDcActual"]?.[index]));
                                }) : data["EssDischarge"];
                        },
                        color: ChartConstants.Colors.RED,
                        stack: 2,
                        ...(chartType === "line" && { order: 5 }),
                    },

                    // Sell to grid
                    {
                        name: translate.instant("GENERAL.GRID_SELL_ADVANCED"),
                        nameSuffix: (energyValues: QueryHistoricTimeseriesEnergyResponse) => energyValues.result.data["_sum/GridSellActiveEnergy"],
                        converter: () => data["GridSell"],
                        color: ChartConstants.Colors.PURPLE,
                        stack: 1,
                        ...(chartType === "line" && { order: 4 }),
                    },

                    // Buy from Grid
                    {
                        name: translate.instant("GENERAL.GRID_BUY_ADVANCED"),
                        nameSuffix: (energyValues: QueryHistoricTimeseriesEnergyResponse) => energyValues.result.data["_sum/GridBuyActiveEnergy"],
                        converter: () => data["GridBuy"],
                        color: ChartConstants.Colors.BLUE_GREY,
                        stack: 2,
                        ...(chartType === "line" && { order: 2 }),
                    },

                    // Consumption
                    {
                        name: translate.instant("GENERAL.CONSUMPTION"),
                        nameSuffix: (energyValues: QueryHistoricTimeseriesEnergyResponse) => energyValues.result.data["_sum/ConsumptionActiveEnergy"],
                        converter: () => data["Consumption"],
                        color: ChartConstants.Colors.YELLOW,
                        stack: 3,
                        hiddenOnInit: chartType == "line" ? false : true,
                        ...(chartType === "line" && { order: 0 }),
                    },
                    ...(chartType === "line" ?
                        [{
                            name: translate.instant("GENERAL.SOC"),
                            converter: () => data["EssSoc"]?.map(value => Utils.multiplySafely(value, 1000)),
                            color: "rgb(189, 195, 199)",
                            borderDash: [10, 10],
                            yAxisId: ChartAxis.RIGHT,
                            stack: 1,
                        } as HistoryUtils.DisplayValue] : []),
                ];
            },
            tooltip: {
                formatNumber: "1.0-2",
                afterTitle: (stack: string) => {

                    if (chartType === "bar") {
                        if (stack === "1") {
                            return translate.instant("GENERAL.PRODUCTION");
                        } else if (stack === "2") {
                            return translate.instant("GENERAL.CONSUMPTION");
                        }
                    }
                    return null;
                },
            },
            yAxes: [

                // Left YAxis
                {
                    unit: YAxisType.ENERGY,
                    position: "left",
                    yAxisId: ChartAxis.LEFT,
                },

                // Right Yaxis, only shown for line-chart
                ...(chartType === "line" ? [{
                    unit: YAxisType.PERCENTAGE,
                    customTitle: "%",
                    position: "right" as const,
                    yAxisId: ChartAxis.RIGHT,
                    displayGrid: false,
                }] : []),
            ],
            normalizeOutputData: true,
        };
    }

    public override getChartData() {
        return ChartComponent.getChartData(this.config, this.chartType, this.translate);
    }

    protected override getChartHeight(): number {
        return this.service.deviceHeight / 1.5;
    }

    protected override async loadChart() {
        this.labels = [];
        this.errorResponse = null;
        const unit = calculateResolution(this.service, this.service.historyPeriod.value.from, this.service.historyPeriod.value.to).resolution.unit;
        // Show Barchart if resolution is days or months
        if (ChronoUnit.isAtLeast(unit, ChronoUnit.Type.DAYS)) {
            Promise.all([
                this.queryHistoricTimeseriesEnergyPerPeriod(this.service.historyPeriod.value.from, this.service.historyPeriod.value.to),
                this.queryHistoricTimeseriesEnergy(this.service.historyPeriod.value.from, this.service.historyPeriod.value.to),
            ]).then(([energyPeriodResponse, energyResponse]) => {
                this.chartType = "bar";
                this.chartObject = this.getChartData();

                // TODO after chartjs migration, look for config
                energyPeriodResponse = DateTimeUtils.normalizeTimestamps(unit, energyPeriodResponse);
                const displayValues = AbstractHistoryChart.fillChart(this.chartType, this.chartObject, energyPeriodResponse, energyResponse);
                this.datasets = displayValues.datasets;
                this.legendOptions = displayValues.legendOptions;
                this.labels = displayValues.labels;
                this.setChartLabel();
            });
        } else {
            // Shows Line-Chart
            Promise.all([
                this.queryHistoricTimeseriesData(this.service.historyPeriod.value.from, this.service.historyPeriod.value.to),
                this.queryHistoricTimeseriesEnergy(this.service.historyPeriod.value.from, this.service.historyPeriod.value.to),
            ])
                .then(([dataResponse, energyResponse]) => {
                    dataResponse = DateTimeUtils.normalizeTimestamps(unit, dataResponse);
                    this.chartType = "line";
                    this.chartObject = this.getChartData();
                    const displayValues = AbstractHistoryChart.fillChart(this.chartType, this.chartObject, dataResponse, energyResponse);
                    this.datasets = displayValues.datasets.map(dataset => ({
                        ...dataset,
                        spanGaps: true,
                    }));
                    this.legendOptions = displayValues.legendOptions;
                    this.labels = displayValues.labels;
                    this.setChartLabel();
                });
        }
    }

    protected override  getChannelAddresses(): Promise<{ powerChannels: ChannelAddress[], energyChannels: ChannelAddress[] }> {
        return new Promise<{ powerChannels: ChannelAddress[], energyChannels: ChannelAddress[] }>(resolve => {
            if (this.chartObject?.input) {
                resolve({
                    powerChannels: this.chartObject.input.map(element => element.powerChannel),
                    energyChannels: this.chartObject.input.map(element => element.energyChannel),
                });
            }
        });
    }

    /**
      * Sends the Historic Timeseries Data Query and makes sure the result is not empty.
      *
      * @param fromDate the From-Date
      * @param toDate   the To-Date
      * @param edge     the current Edge
      * @param ws       the websocket
      */
    protected override queryHistoricTimeseriesData(fromDate: Date, toDate: Date, res?: Resolution): Promise<QueryHistoricTimeseriesDataResponse> {

        this.isDataExisting = true;
        const resolution = res ?? calculateResolution(this.service, fromDate, toDate).resolution;

        const result: Promise<QueryHistoricTimeseriesDataResponse> = new Promise<QueryHistoricTimeseriesDataResponse>((resolve, reject) => {
            this.service.getCurrentEdge().then(edge => {
                this.service.getConfig().then(async () => {
                    const channelAddresses = (await this.getChannelAddresses()).powerChannels;
                    const request = new QueryHistoricTimeseriesDataRequest(DateUtils.maxDate(fromDate, this.edge?.firstSetupProtocol), toDate, channelAddresses, resolution);
                    edge.sendRequest(this.service.websocket, request).then(response => {
                        const result = (response as QueryHistoricTimeseriesDataResponse)?.result;
                        if (Object.keys(result).length != 0) {
                            resolve(response as QueryHistoricTimeseriesDataResponse);
                        } else {
                            this.errorResponse = new JsonrpcResponseError(request.id, { code: 1, message: "Empty Result" });
                            resolve(new QueryHistoricTimeseriesDataResponse(response.id, {
                                timestamps: [null], data: { null: null },
                            }));
                        }
                    }).catch((response) => {
                        this.errorResponse = response;
                        this.initializeChart();
                    });
                });
            });
        }).then((response) => {

            // Check if channelAddresses are empty
            if (Utils.isDataEmpty(response)) {

                // load defaultchart
                this.isDataExisting = false;
                this.initializeChart();
            }
            return response;
        });
        return result;
    }
}

